import { format } from 'date-fns/format';

import {
  balanceFloat,
  balanceFloatDecimals,
  formatCurrency,
  formatFloat,
  removeDemicals,
} from 'helpers/convertNumber';

// Parse a display value (e.g. Greek-formatted "1.240,00") into a number.
function parseNumericValue(value: any): number {
  if (value === null || value === undefined || value === '') return NaN;
  const parsed = parseFloat(
    String(value)
      .replace(/[^\d,.-]/g, '')
      .replace(/\./g, '')
      .replace(',', '.'),
  );
  return Number.isNaN(parsed) ? NaN : parsed;
}

// Parse a display date value into a timestamp. Supports dd/mm/yyyy and ISO-like.
function parseDateValue(value: any): number {
  if (value === null || value === undefined || value === '') return NaN;
  const str = String(value).trim().split(' ')[0];
  const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    const year = y.length === 2 ? Number(`20${y}`) : Number(y);
    return new Date(year, Number(m) - 1, Number(d)).getTime();
  }
  const t = new Date(str.replace(/-/g, '/')).getTime();
  return Number.isNaN(t) ? NaN : t;
}

// react-table sortType that pushes empty/unparseable values to the bottom
// and compares the rest numerically.
function makeSortType(parse: (value: any) => number) {
  return (rowA: any, rowB: any, columnId: string) => {
    const a = parse(rowA?.values?.[columnId]);
    const b = parse(rowB?.values?.[columnId]);
    const aNaN = Number.isNaN(a);
    const bNaN = Number.isNaN(b);
    if (aNaN && bNaN) return 0;
    if (aNaN) return 1;
    if (bNaN) return -1;
    if (a === b) return 0;
    return a > b ? 1 : -1;
  };
}

const numberSortType = makeSortType(parseNumericValue);
const dateSortType = makeSortType(parseDateValue);

// Resolve a type-aware react-table sortType for a given column type.
// Falls back to the default (alphanumeric) sort for text/unknown types.
function getSortTypeForColumn(type: any) {
  const normalized = String(type || '').toLowerCase();
  if (['float', 'number', 'int', 'integer', 'currency'].includes(normalized)) {
    return numberSortType;
  }
  if (['date', 'datetime', 'time'].includes(normalized)) {
    return dateSortType;
  }
  return undefined;
}

export const createTableColumns = (keyData: any, accessor: any, header: any) => {
  return keyData.map((column: any) => {
    const sortType = getSortTypeForColumn(column.type);
    return {
      type: column.type,
      Header: column[header] || column[accessor],
      accessor: column[accessor].replace(/\./g, '_'),
      ...(sortType ? { sortType } : {}),
      ...(column.FIELDWIDTH
        ? { width: Number(column.FIELDWIDTH), minWidth: Number(column.FIELDWIDTH) }
        : {}),
    };
  });
};

function getValidAccessor(prop: any) {
  // Data prop cant have property including dot notation
  // so we replace it with '-'
  // it must be the same with accessor of columns
  return prop.replace(/\./g, '_');
}

/* ------------------ Memory Table Functions ------------------- */
export const createListData = (valueData: any, keyData: any) => {
  return valueData.map((row: any) =>
    keyData.reduce((obj: any, cell: any, index: number) => {
      const accessor = getValidAccessor(cell.name);
      if (cell.type === 'DateTime') {
        return { ...obj, [accessor]: row[index]?.split(' ')[0] };
      }
      return { ...obj, [accessor]: row[index] };
    }, {}),
  );
};

/* ---------------------- Detail Table functions ------------------ */
function convertDetailValue({
  FIELDTYPE,
  FIELDPROPERTY,
  DISPLAYVALUE,
  NUMOFDECIMAL,
}: any) {
  if (FIELDTYPE === 'Bool') return DISPLAYVALUE === '1' ? 'Yes' : 'No';
  if (FIELDTYPE === 'Text') {
    if (FIELDPROPERTY === 'Integer') {
      const formatedNumber = formatCurrency(DISPLAYVALUE, NUMOFDECIMAL);
      return DISPLAYVALUE ? removeDemicals(formatedNumber) : '';
    }
    if (FIELDPROPERTY === 'Number') {
      const resolvedDecimals = Number(NUMOFDECIMAL) > 0 ? Number(NUMOFDECIMAL) : 2;
      const formatedNumber = formatCurrency(DISPLAYVALUE, resolvedDecimals);
      return DISPLAYVALUE ? balanceFloatDecimals(formatedNumber, resolvedDecimals) : '';
    }
  }
  if (FIELDTYPE === 'Time') {
    if (DISPLAYVALUE == null || DISPLAYVALUE === '') return '';
    const d =
      DISPLAYVALUE instanceof Date
        ? DISPLAYVALUE
        : new Date(String(DISPLAYVALUE).replace(/-/g, '/'));
    return Number.isNaN(d.getTime()) ? String(DISPLAYVALUE) : format(d, 'HH:mm');
  }
  return DISPLAYVALUE || '';
}
export const createDetailData = (valueData: any, property: any, eopyyInfo: any) => {
  // create this values only once as they dont depent on store-row
  const fields = {
    eopyy: valueData[0].item
      .filter((curItem: any) => curItem.FIELDTYPE === 'Eopyy')
      .map((curItem: any) => ({ ...curItem, eopyyInfo })),
    date: valueData[0].item.filter((cur: any) => cur.FIELDTYPE === 'Date'),
    time: valueData[0].item.filter((cur: any) => cur.FIELDTYPE === 'Time'),
    string: valueData[0].item.filter((cur: any) => cur.FIELDTYPE === 'String'),
    list: valueData[0].item.filter((cur: any) => cur.FIELDTYPE === 'List'),
    link: valueData[0].item.filter((cur: any) => cur.FIELDTYPE === 'Link'),
    readOnly: valueData[0].item.filter((cur: any) => cur.FIELDREADONLY),
    editable: valueData[0].item.filter((cur: any) => cur.EDITABLE),
    counter: valueData[0].item.filter(
      (cur: any) =>
        (cur.FIELDPROPERTY === 'Number' || cur.FIELDPROPERTY === 'Integer') &&
        cur.EDITABLE,
    ),
  };

  return valueData.map((store: any) => {
    return store.item.reduce(
      (acc: any, item: any) => {
        const accessor = getValidAccessor(item[property]);
        const detailValue = convertDetailValue(item);
        return { ...acc, [accessor]: detailValue };
      },
      { ID: store.id, fields, ISGIFT: store.ISGIFT },
    );
  });
};

/* ------------------- List and Search Table share the same functions ------------------------ */

function combineData(data: any, keyData: any) {
  return keyData.reduce((acc: any, column: any, index: number) => {
    const accessor = getValidAccessor(column.dataIndex);
    if (column.type === 'date') {
      const dateWithoutTime = data[index + 1].split(' ')[0];
      return { ...acc, [accessor]: dateWithoutTime };
    }
    if (column.type === 'float') {
      const formatedNumber = formatFloat(data[index + 1]);
      return { ...acc, [accessor]: formatedNumber };
    }
    return { ...acc, [accessor]: data[index + 1] };
  }, {});
}
export const createTableData = (valueData: any, keyData: any) => {
  return valueData.map((data: any) => {
    const id = data[0].split(';').pop();
    const newObj = combineData(data, keyData);
    return { ID: id, ...newObj };
  });
};
